import React from 'react';
import Box from '@mui/material/Box';
import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { SxProps, Theme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useColors } from '../theme/ColorModeContext';
import { RESEARCHZERO_URL } from './brand';

interface BrandLockupProps {
  align?: 'left' | 'center';
  compact?: boolean;
  showByline?: boolean;
  showTagline?: boolean;
  size?: 'small' | 'medium' | 'large';
  sx?: SxProps<Theme>;
  to?: string;
}

const sizeToFont = {
  small: '15px',
  medium: '22px',
  large: '30px',
};

const BrandLockup = ({
  align = 'left',
  compact = false,
  showByline = true,
  showTagline = false,
  size = 'medium',
  sx,
  to = '/dashboard',
}: BrandLockupProps) => {
  const colors = useColors();

  const logoSx = {
    color: colors.text.primary,
    display: 'inline-flex',
    alignItems: 'baseline',
    textDecoration: 'none',
    '&:hover': {
      color: colors.text.primary,
    },
  };

  const logoText = compact ? (
    <span style={{ color: colors.accent.cyan }}>W3</span>
  ) : (
    <>
      WEB<span style={{ color: colors.accent.cyan, display: 'inline-block' }}>3</span>PWN
    </>
  );

  const logo = to.startsWith('http') ? (
    <Box component="a" href={to} target="_blank" rel="noopener noreferrer" sx={logoSx}>
      {logoText}
    </Box>
  ) : (
    <Box component={RouterLink} to={to} sx={logoSx}>
      {logoText}
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: align === 'center' ? 'center' : 'flex-start',
        textAlign: align,
        gap: compact ? 0 : '3px',
        minWidth: 0,
        // Isolates this block in its own compositing layer so the byline/tagline
        // survive an ancestor's page-enter animation (Chrome/WebKit can drop the
        // repaint of nearby content once that animation's layer decomposites);
        // see the identical workaround on the "Sign In" heading in Login.tsx.
        transform: 'translateZ(0)',
        willChange: 'transform',
        ...sx as any,
      }}
    >
      <Typography
        variant="h5"
        noWrap
        component="div"
        sx={{
          fontFamily: '"Orbitron", sans-serif',
          fontWeight: 800,
          letterSpacing: 0,
          fontSize: compact ? sizeToFont.small : sizeToFont[size],
          lineHeight: 1,
          textTransform: 'uppercase',
          transition: 'font-size 0.3s ease',
        }}
      >
        {logo}
      </Typography>

      {!compact && showByline && (
        <MuiLink
          href={RESEARCHZERO_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="ResearchZero website"
          sx={{
            color: colors.text.muted,
            fontFamily: '"Share Tech Mono", monospace',
            fontSize: size === 'large' ? '0.72rem' : '0.56rem',
            lineHeight: 1,
            textDecoration: 'none',
            textTransform: 'uppercase',
            '&:hover': {
              color: colors.accent.cyan,
            },
          }}
        >
          by ResearchZero
        </MuiLink>
      )}

      {!compact && showTagline && (
        <Typography
          variant="body2"
          sx={{
            color: colors.text.secondary,
            lineHeight: 1.5,
            marginTop: '10px',
            maxWidth: '320px',
          }}
        >
          A{' '}
          <MuiLink
            href={RESEARCHZERO_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: colors.accent.cyan, fontWeight: 600, textDecoration: 'none' }}
          >
            ResearchZero
          </MuiLink>{' '}
          training lab for smart contract exploitation and Web3 security practice.
        </Typography>
      )}
    </Box>
  );
};

export default BrandLockup;
